import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { getCommunityUsers } from '../../../../../services/communityService';
import { fetchUsers } from '../../../../../services/userService';

/**
 * Mobile-optimized modal for adding attendees to an event
 * 
 * @param {boolean} show - Whether to show the modal
 * @param {function} onHide - Function to hide the modal
 * @param {object} event - Event data
 * @param {array} existingAttendees - Array of existing attendees
 * @param {function} onAddAttendees - Function to add attendees
 * @returns {JSX.Element} Modal component
 */
const MobileAttendeesModal = ({
  show,
  onHide,
  event,
  existingAttendees,
  onAddAttendees
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [communityName, setCommunityName] = useState('');

  // Load available users
  useEffect(() => {
    const loadUsers = async () => {
      if (!show) return; // Only load when modal is shown
      
      setLoading(true);
      setError('');
      try {
        // Get the community ID from the event
        const communityId = event?.community_id;
        
        // Fetch users
        let users = [];
        
        if (communityId) {
          // For events with a community, fetch community users
          users = await getCommunityUsers(communityId);
          setCommunityName(`Skupnost ${communityId}`); // Set a default name
          
          // Try to get proper community name from the event
          if (event.community_name) {
            setCommunityName(event.community_name);
          }
        } else {
          // For events without a community, fetch users without community
          users = await fetchUsers();
          users = users.filter(user => !user.community_id);
          setCommunityName('Brez skupine');
        }
        
        setAvailableUsers(users || []);
      } catch (error) {
        setError('Napaka pri nalaganju uporabnikov');
      } finally {
        setLoading(false);
      }
    };
    
    loadUsers();
  }, [show, event]);

  // Filter users based on search term and existing attendees
  useEffect(() => {
    if (!availableUsers.length) {
      setFilteredUsers([]);
      return;
    }
    
    let filtered = [...availableUsers];
    
    // Filter out users that are already attendees
    if (existingAttendees && existingAttendees.length > 0) {
      const existingUserIds = existingAttendees.map(att => 
        typeof att.user_id === 'string' ? att.user_id : att.user_id.toString()
      );
      
      filtered = filtered.filter(user => 
        !existingUserIds.includes(typeof user.id === 'string' ? user.id : user.id.toString())
      );
    }
    
    // Filter by search term
    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(user =>
        user.display_name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term)
      );
    }
    
    setFilteredUsers(filtered);
  }, [availableUsers, existingAttendees, searchTerm]);

  // Handle user selection
  const handleUserSelect = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // Handle adding selected users
  const handleAddUsers = async () => {
    if (selectedUsers.length === 0) return;
    
    setSaving(true);
    setError('');
    
    try {
      await onAddAttendees(selectedUsers);
      setSelectedUsers([]);
      onHide(); // Close modal after successful addition
    } catch (error) {
      setError('Napaka pri dodajanju udeležencev');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      fullscreen="sm-down"
      className="attendees-modal-mobile"
    >
      <Modal.Header closeButton className="bg-dark text-white border-secondary">
        <Modal.Title className="fs-6">Dodaj udeležence</Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="bg-dark text-white">
        {error && (
          <div className="alert alert-danger py-2 mb-3">
            {error}
          </div>
        )}
        
        {/* Community context information */}
        <div className="alert alert-info py-2 mb-3">
          <small>
            {loading ? 'Nalaganje podatkov...' : `Prikazani so uporabniki iz "${communityName}"`}
          </small>
        </div>
        
        {/* Search input */}
        <Form.Group className="mb-3">
          <div className="input-group">
            <span className="input-group-text bg-dark text-white border-secondary">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <Form.Control
              type="text"
              placeholder="Išči uporabnike..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-dark text-white border-secondary"
            />
          </div>
        </Form.Group>
        
        {/* User selection list */}
        <div className="user-selection">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-3 text-muted">
              {availableUsers.length === 0 
                ? "Ni razpoložljivih uporabnikov" 
                : "Ni zadetkov za iskalni niz ali so že vsi dodani"}
            </div>
          ) : (
            <ListGroup className="available-users-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {filteredUsers.map(user => (
                <ListGroup.Item 
                  key={user.id}
                  className="py-2 bg-dark text-white border-secondary"
                  action
                  onClick={() => handleUserSelect(user.id)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      {/* User avatar */}
                      <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                          style={{ width: '32px', height: '32px' }}>
                          {(user.display_name || user.email?.split('@')[0] || '?').charAt(0).toUpperCase()}
                      </div>

                      <span>{user.display_name || user.email?.split('@')[0]}</span>
                    </div>
                    
                    {/* Checkbox */}
                    <Form.Check 
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => {}} // Handled by the ListGroup.Item onClick
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
      </Modal.Body>
      
      <Modal.Footer className="bg-dark text-white border-secondary d-flex justify-content-center">
        <Button variant="outline-secondary" onClick={onHide} size="sm" className="mx-2">
          Prekliči
        </Button>
        <Button 
          variant="primary" 
          onClick={handleAddUsers} 
          disabled={selectedUsers.length === 0 || saving}
          size="sm"
          className="mx-2"
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Dodajanje...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faUserPlus} className="me-1" />
              Dodaj izbrane ({selectedUsers.length})
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MobileAttendeesModal;